import {
  Box,
  Divider,
  Flex,
  Radio,
  RadioGroup,
  Text,
  Textarea
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FieldLabel } from '../Field';
import {
  Button,
  DatePicker,
  FormFeedback,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import MultiSelect from 'src/components/MultiSelect';
import { ISubmissionContextJournal } from '@publishing-engine/domain/views';
import { zodResolver } from '@hookform/resolvers/zod';
import { vendorExportAdvanceConfigurationSchema } from '@publishing-engine/domain/validation/vendorExportRetryFormValidation';
import { IExportAdvanceConfigurationFormValues } from '@publishing-engine/domain/models/vendor-exports';
import { useRetryBulkExports, useValidateManuscripts } from 'src/api/mutations';
import { useState } from 'react';
interface AdvanceConfigurationVendorExportProps {
  selectedJournal: ISubmissionContextJournal;
  setIsOpen: (arg0: boolean) => void;
  orgId: string;
}
const AdvanceConfigurationVendorExport = ({
  selectedJournal,
  setIsOpen,
  orgId
}: AdvanceConfigurationVendorExportProps) => {
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [manuscriptIdsByDateRange, setManuscriptIdsByDateRange] = useState<
    string[]
  >([]);
  const methods = useForm<IExportAdvanceConfigurationFormValues>({
    resolver: zodResolver(vendorExportAdvanceConfigurationSchema),
    mode: 'onSubmit',
    defaultValues: {
      exportName: [],
      manuscriptIds: '',
      exportStatus: undefined
    }
  });
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { isSubmitting, errors, isValid }
  } = methods;
  const exportName = watch('exportName');
  const exportBy = watch('exportBy');
  const exportStatus = watch('exportStatus');
  const ids = watch('manuscriptIds');

  const toast = useToast();
  const { retryBulkExportsAsync } = useRetryBulkExports(
    selectedJournal.id,
    orgId
  );

  const onSubmit = async (values: IExportAdvanceConfigurationFormValues) => {
    const selectedConfigs =
      selectedJournal.config?.exportConfig?.filter(config =>
        values.exportName.includes(config.name)
      ) ?? [];
    const payload = {
      exportConfig: selectedConfigs!,
      exportConfigId: selectedConfigs?.map(
        selectedConfigs => selectedConfigs.id
      ),
      manuscriptIds: values.manuscriptIds,
      exportStatus: values.exportStatus,
      fromDate: values.fromDate,
      toDate: values.toDate,
      exportBy: values.exportBy
    };
    const response = await retryBulkExportsAsync(payload);
    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: `Retry triggered successfully for ${response.submissionCount} submissions for ${response.exportCount} configurations`
      });
    } else {
      toast({
        variant: 'error',
        title: 'Failed to trigger retry'
      });
    }
    setIsOpen(false);
  };

  const { validateManuscriptIdsAsync } = useValidateManuscripts(
    selectedJournal?.id,
    orgId
  );

  const handleContinueClick = handleSubmit(async values => {
    if (values.exportBy === 'manuscriptIds') {
      const response = await validateManuscriptIdsAsync({
        exportBy: 'manuscriptIds',
        manuscriptIds: values.manuscriptIds
      });
      if (response.isSuccess && response.missingManuscriptIds) {
        if (response.valid) {
          toast({
            variant: 'success',
            title: 'Validated manuscripts successfully'
          });
          setIsFormOpen(false);
        } else {
          toast({
            variant: 'error',
            title: 'Some manuscript ID(s) are invalid',
            description: response.missingManuscriptIds.join(', ')
          });
        }
      } else {
        toast({
          variant: 'error',
          title: 'Failed to validate manuscripts'
        });
      }
    } else if (values.exportBy === 'dateRange') {
      const response = await validateManuscriptIdsAsync({
        exportBy: 'dateRange',
        exportStatus: values.exportStatus,
        fromDate: values.fromDate,
        toDate: values.toDate
      });
      if (response.isSuccess && response.manuscriptIdsForDateRange) {
        if (response.valid) {
          toast({
            variant: 'success',
            title: 'Validated manuscripts successfully'
          });
          setManuscriptIdsByDateRange(response.manuscriptIdsForDateRange ?? []);
          setIsFormOpen(false);
        } else {
          toast({
            variant: 'error',
            title: 'Some manuscript ID(s) are invalid'
          });
        }
      } else {
        toast({
          variant: 'error',
          title: 'Failed to validate manuscripts'
        });
      }
      setIsFormOpen(false);
    }
  });
  return (
    <Box p={{ base: 3, md: 5 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Text textStyle="headingGroup">Advance Bulk Operation</Text>
        <Text textStyle="textBody">Retrigger the exports for the vendors</Text>
        <Divider mt={3} mb={3} />
        {isFormOpen ? (
          <Box mt={4}>
            <Flex direction={'column'} gap={3}>
              <Flex direction={'column'} width={{ base: '100%', md: '50%' }}>
                <FieldLabel subText="" label="Export name(s)" isRequired />
                <MultiSelect
                  options={
                    selectedJournal?.config?.exportConfig?.map(config => ({
                      value: config.name,
                      label: config.name
                    })) ?? []
                  }
                  value={watch('exportName') ?? []}
                  variant="interactive"
                  onChange={val =>
                    setValue('exportName', val, { shouldValidate: true })
                  }
                  placeholder="Select the export name(s)"
                />
                {errors.exportName && (
                  <FormFeedback variant="error">
                    {errors.exportName?.message}
                  </FormFeedback>
                )}
              </Flex>
              <Flex direction={'column'}>
                <FieldLabel label="Re-export By" subText="" />
                <RadioGroup
                  value={exportBy}
                   onChange={value =>
                    setValue('exportBy', value as 'manuscriptIds' | 'dateRange')
                  }
                  name="exportBy"
                >
                  <Flex gap={4}>
                    <Radio value="manuscriptIds">Manuscript ID(s)</Radio>
                    <Radio value="dateRange">
                      Export status and date range
                    </Radio>
                  </Flex>
                </RadioGroup>
                {errors.exportBy && (
                  <FormFeedback variant="error">
                    {errors.exportBy?.message}
                  </FormFeedback>
                )}
              </Flex>
              {exportBy && (
                <OutlineCard  padding={3}>
                  {exportBy === 'manuscriptIds' && (
                    <Flex direction="column" width="100%">
                      <FieldLabel
                        subText="Enter manuscript IDs separated by commas"
                        label="Manuscript ID(s)"
                        isRequired
                      />
                      <Textarea
                        {...register('manuscriptIds')}
                        bg={'white'}
                        placeholder="Enter the manuscript ID(s)"
                      />
                      {errors.manuscriptIds && (
                        <FormFeedback variant="error">
                          {errors.manuscriptIds?.message}
                        </FormFeedback>
                      )}
                    </Flex>
                  )}
                  {exportBy === 'dateRange' && (
                    <>
                      <Flex direction={'column'} mt={3}>
                        <FieldLabel
                          subText=""
                          label="Export status"
                          isRequired
                        />
                        <RadioGroup
                          value={exportStatus}
                          onChange={value =>
                            setValue('exportStatus', value as 'all' | 'failed')
                          }
                        >
                          <Flex gap={4}>
                            <Radio value="all">All</Radio>
                            <Radio value="failed">Failed</Radio>
                          </Flex>
                        </RadioGroup>
                        {errors.exportStatus && (
                          <FormFeedback variant="error">
                            {errors.exportStatus?.message}
                          </FormFeedback>
                        )}
                      </Flex>
                      <Flex direction={'column'} mt={3}>
                        <FieldLabel subText="" label="Select the date range" />
                        <Flex direction={{ base: 'column', md: 'row' }} gap={2}>
                          <Flex direction={'column'} width={'100%'}>
                            <FieldLabel subText="" label="From" />
                            <DatePicker {...register('fromDate')} />
                            <Text textStyle={'textContext'}>
                              Select a range of upto 15 days
                            </Text>
                            {errors.fromDate && (
                              <FormFeedback variant="error">
                                {errors.fromDate.message}
                              </FormFeedback>
                            )}
                          </Flex>
                          <Flex direction={'column'} width={'100%'}>
                            <FieldLabel subText="" label="To" />
                            <DatePicker
                              {...register('toDate')}
                              max={new Date().toISOString().split('T')[0]}
                            />
                            {errors.toDate && (
                              <FormFeedback variant="error">
                                {errors.toDate.message}
                              </FormFeedback>
                            )}
                          </Flex>
                        </Flex>
                      </Flex>
                    </>
                  )}
                </OutlineCard>
              )}
              {/* Buttons */}
              <Flex justifyContent={'flex-end'} gap={3} mt={3}>
                <Button variant="secondary" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant={'primary'}
                  onClick={async () => handleContinueClick()}
                  disabled={!isValid}
                >
                  Continue
                </Button>
              </Flex>
            </Flex>
          </Box>
        ) : (
          <Flex direction={'column'} gap={3}>
            <Flex direction={'column'}>
              <Text fontWeight={'bold'}>Export Name(s)</Text>
              <Text>{exportName.join(', ')}</Text>
            </Flex>
            <Flex direction={'column'}>
              <Text fontWeight={'bold'}>Number of Manuscripts</Text>
              <Text>
                {exportBy === 'manuscriptIds'
                  ? ids.split(',').length
                  : manuscriptIdsByDateRange.length}
              </Text>
            </Flex>
            <Flex direction={'column'}>
              <Text fontWeight={'bold'}>Manuscript ID(s)</Text>
              <Text>
                {exportBy === 'manuscriptIds'
                  ? ids || 'No manuscripts'
                  : manuscriptIdsByDateRange?.length
                  ? manuscriptIdsByDateRange.join(', ')
                  : 'No manuscripts found'}
              </Text>
            </Flex>
            <Flex justifyContent={'space-between'}>
              <Button variant={'secondary'} onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Flex gap={2}>
                <Button
                  variant={'secondary'}
                  onClick={() => setIsFormOpen(true)}
                >
                  Edit
                </Button>
                <Button
                  type="submit"
                  variant={'primary'}
                  isLoading={isSubmitting}
                  disabled={
                    (exportBy === 'manuscriptIds' && !ids?.trim()) ||
                    (exportBy === 'dateRange' &&
                      manuscriptIdsByDateRange.length === 0)
                  }
                >
                  Retrigger Exports
                </Button>
              </Flex>
            </Flex>
          </Flex>
        )}
      </form>
    </Box>
  );
};

export default AdvanceConfigurationVendorExport;
